import React from "react";
import "./InnerInput.scss";
import Tooltip from "../../Tooltip";
import cn from 'clsx';

interface Props {
  label?: string;
  placeholder?: string;
  value: string;
  type: string;
  id: string;
  onChange: (e: string) => void;
  hasError?: boolean;
  disabled?: boolean;
  isRequired?: boolean;
  icon?: string;
  onBlur?: () => void;
  rows?: number;
  title?: string;
  inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"];
  suffix?: string;
  min?: number;
  max?: number;
  step?: string | number;
  maxCodePoints?: number;
  tooltipText?: string;
  tooltipPosition?:
    | "top"
    | "right"
    | "bottom"
    | "left"
    | "top-right"
    | "top-left"
    | "bottom-left"
    | "bottom-right";
}

const InnerInput: React.FC<Props> = ({
  label,
  placeholder,
  value,
  type,
  id,
  onChange,
  disabled,
  hasError,
  isRequired,
  icon,
  onBlur,
  rows = 5,
  tooltipText,
  tooltipPosition,
  title,
  inputMode,
  suffix,
  min,
  max,
  step,
  maxCodePoints,
}) => {
  const limitValue = (next: string) => {
    if (!maxCodePoints || maxCodePoints < 1) {
      return next;
    }
    const points = Array.from(next);
    if (points.length <= maxCodePoints) {
      return next;
    }
    return points.slice(0, maxCodePoints).join("");
  };

  return (
    <div
      className={cn(
      "inner-input",
      hasError && "inner-input--error",
      disabled && "inner-input--disabled"
    )}
    >
      {label && (
        <label htmlFor={id} className="inner-input__label">
          {icon && <img src={icon} alt="" className="inner-input__icon" />}
          {label}
          {isRequired && <span className="inner-input__required">*</span>}
          {tooltipText && (
            <Tooltip text={tooltipText} position={tooltipPosition}>
              <button className="info-icon">ℹ</button>
            </Tooltip>
          )}
        </label>
      )}

      {type === "textarea" ? (
        <textarea
          id={id}
          disabled={disabled}
          value={value}
          placeholder={placeholder ?? ""}
          className="inner-input__field inner-input__field--textarea"
          onChange={(e) => onChange(limitValue(e.target.value))}
          onBlur={onBlur}
          rows={rows}
          title={title}
        />
      ) : (
          <div
            className={cn("inner-input__input-group", suffix && "inner-input__input-group--with-suffix")}
        >
          <input
            id={id}
            type={type}
            disabled={disabled}
            value={value}
            placeholder={placeholder ?? ""}
            className="inner-input__field"
            onChange={(e) => onChange(limitValue(e.target.value))}
            onBlur={onBlur}
            title={title}
            inputMode={inputMode}
            step={step}
              {...(inputMode && ['numeric', 'decimal'].includes(inputMode) && { min, max })}
          />
          {suffix && (
            <span className="inner-input__suffix" aria-hidden="true">
              {suffix}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default InnerInput;
